import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X, Plus, Trophy, Users, ScrollText, Save, Inbox, Trash2, Phone, Mail, MapPin, Pencil, Calendar, MessageSquare, ChevronUp, ChevronDown } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { FileUpload } from "@/components/admin/FileUpload";
import { BraggersStatsUpload } from "@/components/admin/BraggersStatsUpload";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

function SingleImage({ value, onChange, label, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div data-testid={testid}>
      {label && <label className="block text-sm font-medium text-[#333333] mb-1">{label}</label>}
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative">
            <img src={fileUrl(value)} alt="" className="h-20 w-28 object-cover rounded-md border border-[#E5E7EB]" />
            <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
          </div>
        ) : null}
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="h-20 w-28 inline-flex flex-col items-center justify-center gap-1 text-sm border border-dashed border-[#1E63C4] text-[#1E63C4] rounded-md hover:bg-blue-50 disabled:opacity-60">
          <Upload className="h-4 w-4" /> {busy ? "…" : value ? "Replace" : "Upload"}
        </button>
        <input ref={ref} type="file" accept="image/*" onChange={upload} className="hidden" />
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";
const playersToText = (arr) => (arr || []).map((p) => (typeof p === "string" ? p : `${p.name}${p.fargo ? `, ${p.fargo}` : ""}`)).join("\n");
const textToPlayers = (txt) => (txt || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [name, fargo] = l.split(",").map((s) => s.trim());
  return { name: name || "", fargo: fargo || "" };
});

export function BraggersManager() {
  const [cfg, setCfg] = useState(null);
  const [subs, setSubs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editSubId, setEditSubId] = useState(null);
  const [subDraft, setSubDraft] = useState(null);
  const [savingSub, setSavingSub] = useState(false);

  const load = () => api.get("/admin/braggers").then((r) => setCfg(r.data)).catch(() => {});
  const loadSubs = () => api.get("/admin/braggers/submissions").then((r) => setSubs(r.data.submissions || [])).catch(() => {});
  useEffect(() => { load(); loadSubs(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/braggers", cfg);
      setCfg(data);
      toast.success("Bragger's Cup saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const delSub = async (id) => {
    if (!window.confirm("Delete this challenge submission?")) return;
    try { await api.delete(`/admin/braggers/submissions/${id}`); loadSubs(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const startSubEdit = (s) => {
    setEditSubId(s.id);
    setSubDraft({
      contact_name: s.contact_name || "",
      phone: s.phone || "",
      email: s.email || "",
      home_location: s.home_location || "",
      comment: s.comment || "",
      players: (s.players || []).map((p) => ({ name: p.name || "", fargo: p.fargo || "", robustness: p.robustness || "" })),
    });
  };
  const cancelSubEdit = () => { setEditSubId(null); setSubDraft(null); };
  const setSub = (k, v) => setSubDraft((d) => ({ ...d, [k]: v }));
  const setSubPlayer = (i, k, v) => setSubDraft((d) => ({ ...d, players: d.players.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const addSubPlayer = () => setSubDraft((d) => ({ ...d, players: [...d.players, { name: "", fargo: "", robustness: "" }] }));
  const removeSubPlayer = (i) => setSubDraft((d) => ({ ...d, players: d.players.filter((_, idx) => idx !== i) }));

  const saveSubEdit = async (id) => {
    if (!subDraft.contact_name.trim() || !subDraft.phone.trim() || !subDraft.email.trim()) {
      toast.error("Name, phone and email are required.");
      return;
    }
    setSavingSub(true);
    try {
      const payload = { ...subDraft, players: subDraft.players.filter((p) => p.name.trim()) };
      const { data } = await api.put(`/admin/braggers/submissions/${id}`, payload);
      setSubs((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success("Submission updated");
      cancelSubEdit();
    } catch {
      toast.error("Could not save submission");
    } finally {
      setSavingSub(false);
    }
  };

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#1E63C4]" /></div>;

  const challengers = cfg.challengers || [];
  const champions = cfg.champions || [];
  const rules = cfg.rules || [];

  const setChallenger = (i, k, v) => set("challengers", challengers.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const setChampion = (i, k, v) => set("champions", champions.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const moveChampion = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= champions.length) return;
    const next = [...champions];
    [next[i], next[j]] = [next[j], next[i]];
    set("champions", next);
  };

  return (
    <div className="space-y-6" data-testid="braggers-manager">
      <div className="flex items-center justify-between gap-3 sticky top-16 bg-[#F9FAF8] py-2 z-10">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-[#D4AF37]" />
          <h2 className="font-heading font-bold text-xl">Bragger's Cup</h2>
        </div>
        <button onClick={save} disabled={saving} data-testid="braggers-save-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </div>

      <BraggersStatsUpload />

      {/* General */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6 space-y-4">
        <h3 className="font-semibold">Page content</h3>
        <label className="flex items-center gap-3 p-3 rounded-md border border-[#E5E7EB] bg-[#F9FAF8] cursor-pointer" data-testid="braggers-hidden-toggle">
          <input type="checkbox" checked={!cfg.hidden} onChange={(e) => set("hidden", !e.target.checked)} className="h-4 w-4 accent-[#1E63C4]" />
          <span className="text-sm">
            <span className="font-medium text-[#111]">Show Bragger's Cup on the site</span>
            <span className="block text-[#777]">Displays the crest logo link on the homepage and the footer link. Uncheck to hide both (the page stays reachable by direct link).</span>
          </span>
        </label>
        <SingleImage value={cfg.logo} onChange={(v) => set("logo", v)} label="Logo / crest" testid="braggers-logo-upload" />
        <div>
          <label className="block text-sm font-medium text-[#333333] mb-1">Intro / tagline</label>
          <textarea rows={2} className={inputCls} value={cfg.intro || ""} onChange={(e) => set("intro", e.target.value)} data-testid="braggers-intro" />
        </div>
        <SingleImage value={cfg.current_match_poster} onChange={(v) => set("current_match_poster", v)} label="Current match poster" testid="braggers-poster-upload" />
        <div>
          <label className="block text-sm font-medium text-[#333333] mb-1">Current match caption</label>
          <input className={inputCls} value={cfg.current_match_caption || ""} onChange={(e) => set("current_match_caption", e.target.value)} data-testid="braggers-caption" />
        </div>
      </div>

      {/* Rules */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-3"><ScrollText className="h-4 w-4 text-[#1E63C4]" /><h3 className="font-semibold">Rules / guidelines</h3></div>
        <div className="mb-4 p-3 rounded-md border border-[#E5E7EB] bg-[#F9FAF8]">
          <FileUpload label="Complete Rules / Format (PDF)" value={cfg.rules_pdf} onChange={(v) => set("rules_pdf", v)} testid="braggers-rules-pdf" />
          <p className="text-xs text-[#777] mt-1.5">Shown as a downloadable link at the top of the Rules section on the public page.</p>
        </div>
        <div className="space-y-2">
          {rules.map((r, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-sm text-[#999] w-5 pt-2">{i + 1}.</span>
              <textarea rows={2} className={inputCls + " resize-y"} value={r} onChange={(e) => set("rules", rules.map((x, idx) => idx === i ? e.target.value : x))} data-testid={`braggers-rule-${i}`} />
              <button type="button" onClick={() => set("rules", rules.filter((_, idx) => idx !== i))} className="text-[#999] hover:text-red-500 p-2"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => set("rules", [...rules, ""])} data-testid="braggers-add-rule" className="mt-3 inline-flex items-center gap-1 text-sm text-[#1E63C4] font-medium"><Plus className="h-4 w-4" /> Add rule</button>
      </div>

      {/* Challengers */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-3"><Users className="h-4 w-4 text-[#1E63C4]" /><h3 className="font-semibold">Challengers in the queue</h3></div>
        <div className="space-y-4">
          {challengers.map((c, i) => (
            <div key={i} className="border border-[#E5E7EB] rounded-md p-4 grid sm:grid-cols-2 gap-3 relative" data-testid={`braggers-challenger-row-${i}`}>
              <input className={inputCls} placeholder="Team name" value={c.team_name || ""} onChange={(e) => setChallenger(i, "team_name", e.target.value)} />
              <input className={inputCls} placeholder="Captain" value={c.captain || ""} onChange={(e) => setChallenger(i, "captain", e.target.value)} />
              <input className={inputCls} placeholder="Home location" value={c.home_location || ""} onChange={(e) => setChallenger(i, "home_location", e.target.value)} />
              <input className={inputCls} placeholder="Match date (e.g. Sat, Aug 9)" value={c.date || ""} onChange={(e) => setChallenger(i, "date", e.target.value)} />
              <input className={inputCls + " sm:col-span-2"} placeholder="Note (optional)" value={c.note || ""} onChange={(e) => setChallenger(i, "note", e.target.value)} />
              <button type="button" onClick={() => set("challengers", challengers.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => set("challengers", [...challengers, {}])} data-testid="braggers-add-challenger" className="mt-3 inline-flex items-center gap-1 text-sm text-[#1E63C4] font-medium"><Plus className="h-4 w-4" /> Add challenger</button>
      </div>

      {/* Champions */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-3"><Trophy className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-semibold">Champions</h3></div>
        <div className="space-y-6">
          {champions.map((c, i) => (
            <div key={i} className="border border-[#E5E7EB] rounded-md p-4 space-y-3 relative" data-testid={`braggers-champion-row-${i}`}>
              <div className="absolute -top-3 left-3 flex items-center gap-1">
                <span className="bg-white border border-[#E5E7EB] rounded px-2 py-0.5 text-xs font-semibold text-[#555]">#{i + 1}</span>
                <button type="button" onClick={() => moveChampion(i, -1)} disabled={i === 0} data-testid={`braggers-champ-up-${i}`} title="Move up" className="bg-white border border-[#E5E7EB] rounded p-1 hover:bg-[#F9FAF8] disabled:opacity-40 disabled:cursor-not-allowed"><ChevronUp className="h-3.5 w-3.5 text-[#555]" /></button>
                <button type="button" onClick={() => moveChampion(i, 1)} disabled={i === champions.length - 1} data-testid={`braggers-champ-down-${i}`} title="Move down" className="bg-white border border-[#E5E7EB] rounded p-1 hover:bg-[#F9FAF8] disabled:opacity-40 disabled:cursor-not-allowed"><ChevronDown className="h-3.5 w-3.5 text-[#555]" /></button>
              </div>
              <button type="button" onClick={() => set("champions", champions.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Title (e.g. 2025 Champions)" value={c.title || ""} onChange={(e) => setChampion(i, "title", e.target.value)} />
                <input className={inputCls} placeholder="Date" value={c.date || ""} onChange={(e) => setChampion(i, "date", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <SingleImage value={c.team_photo} onChange={(v) => setChampion(i, "team_photo", v)} label="Team photo" testid={`braggers-champ-photo-${i}`} />
                <SingleImage value={c.scoresheet} onChange={(v) => setChampion(i, "scoresheet", v)} label="Scoresheet image" testid={`braggers-champ-score-${i}`} />
              </div>
              <textarea rows={3} className={inputCls} placeholder="Overview / summary paragraph" value={c.summary || ""} onChange={(e) => setChampion(i, "summary", e.target.value)} />
              <input className={inputCls} placeholder="YouTube match URL" value={c.youtube_url || ""} onChange={(e) => setChampion(i, "youtube_url", e.target.value)} />
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <input className={inputCls + " mb-1"} placeholder="Winning team name" value={c.winning_team || ""} onChange={(e) => setChampion(i, "winning_team", e.target.value)} />
                  <textarea rows={5} className={inputCls} placeholder={"Winning players (one per line)\nName, Fargo"} value={playersToText(c.winning_players)} onChange={(e) => setChampion(i, "winning_players", textToPlayers(e.target.value))} />
                </div>
                <div>
                  <input className={inputCls + " mb-1"} placeholder="Challenger team name" value={c.challenger_team || ""} onChange={(e) => setChampion(i, "challenger_team", e.target.value)} />
                  <textarea rows={5} className={inputCls} placeholder={"Challenger players (one per line)\nName, Fargo"} value={playersToText(c.challenger_players)} onChange={(e) => setChampion(i, "challenger_players", textToPlayers(e.target.value))} />
                </div>
              </div>
            <details className="bg-[#F9FAF8] border border-[#E5E7EB] rounded-md px-3 py-2.5">
              <summary className="text-sm font-semibold text-[#1E63C4] cursor-pointer select-none" data-testid={`braggers-champ-page-toggle-${i}`}>Match page — SEO, status &amp; venue</summary>
              <div className="mt-3 space-y-2.5">
                {c.slug && <p className="text-xs text-[#777]">Permanent URL: <a href={`/braggers/${c.slug}`} target="_blank" rel="noreferrer" className="text-[#1E63C4] hover:underline">/braggers/{c.slug}</a></p>}
                <div className="grid sm:grid-cols-3 gap-2">
                  <input className={inputCls} type="number" min="1" placeholder="Edition # (auto)" value={c.edition || ""} onChange={(e) => setChampion(i, "edition", parseInt(e.target.value || "0", 10) || "")} data-testid={`braggers-champ-edition-${i}`} />
                  <select className={inputCls} value={c.status || "completed"} onChange={(e) => setChampion(i, "status", e.target.value)} data-testid={`braggers-champ-status-${i}`}>
                    <option value="completed">Completed</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="live">Live</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm text-[#333]"><input type="checkbox" checked={c.published !== false} onChange={(e) => setChampion(i, "published", e.target.checked)} className="h-4 w-4 accent-[#1E63C4]" data-testid={`braggers-champ-published-${i}`} /> Published</label>
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <input className={inputCls} placeholder="Venue" value={c.venue || ""} onChange={(e) => setChampion(i, "venue", e.target.value)} data-testid={`braggers-champ-venue-${i}`} />
                  <input className={inputCls} placeholder="City" value={c.city || ""} onChange={(e) => setChampion(i, "city", e.target.value)} data-testid={`braggers-champ-city-${i}`} />
                  <input className={inputCls} placeholder="Province" value={c.province || ""} onChange={(e) => setChampion(i, "province", e.target.value)} data-testid={`braggers-champ-province-${i}`} />
                </div>
                <input className={inputCls} placeholder="SEO title override (optional)" value={c.seo_title || ""} onChange={(e) => setChampion(i, "seo_title", e.target.value)} data-testid={`braggers-champ-seo-title-${i}`} />
                <textarea rows={2} className={inputCls} placeholder="SEO description override (optional)" value={c.seo_description || ""} onChange={(e) => setChampion(i, "seo_description", e.target.value)} data-testid={`braggers-champ-seo-desc-${i}`} />
              </div>
            </details>
          </div>
          ))}
        </div>
        <button type="button" onClick={() => set("champions", [...champions, {}])} data-testid="braggers-add-champion" className="mt-3 inline-flex items-center gap-1 text-sm text-[#1E63C4] font-medium"><Plus className="h-4 w-4" /> Add champion</button>
      </div>

      {/* Contact */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6 grid sm:grid-cols-2 gap-3">
        <h3 className="font-semibold sm:col-span-2">Contact</h3>
        <input className={inputCls} placeholder="Contact email" value={cfg.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="braggers-contact-email" />
        <input className={inputCls} placeholder="Contact phone" value={cfg.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} data-testid="braggers-contact-phone" />
        <input className={inputCls + " sm:col-span-2"} placeholder="Contact note" value={cfg.contact_note || ""} onChange={(e) => set("contact_note", e.target.value)} data-testid="braggers-contact-note" />
      </div>

      {/* Submissions */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-3"><Inbox className="h-4 w-4 text-[#1E63C4]" /><h3 className="font-semibold">Challenge submissions ({subs.length})</h3></div>
        {subs.length ? (
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.id} className="border border-[#E5E7EB] rounded-md p-4" data-testid={`braggers-submission-${s.id}`}>
                {editSubId === s.id ? (
                  <div className="space-y-3" data-testid={`braggers-submission-edit-${s.id}`}>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input className={inputCls} placeholder="Contact name" value={subDraft.contact_name} onChange={(e) => setSub("contact_name", e.target.value)} data-testid={`bsub-name-${s.id}`} />
                      <input className={inputCls} placeholder="Home location" value={subDraft.home_location} onChange={(e) => setSub("home_location", e.target.value)} data-testid={`bsub-home-${s.id}`} />
                      <input className={inputCls} placeholder="Phone" value={subDraft.phone} onChange={(e) => setSub("phone", e.target.value)} data-testid={`bsub-phone-${s.id}`} />
                      <input className={inputCls} placeholder="Email" value={subDraft.email} onChange={(e) => setSub("email", e.target.value)} data-testid={`bsub-email-${s.id}`} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#555] mb-1">Players</label>
                      <div className="space-y-2">
                        {subDraft.players.map((p, i) => (
                          <div key={i} className="flex gap-2">
                            <input className={inputCls + " flex-1 min-w-0"} placeholder={`Player ${i + 1} name`} value={p.name} onChange={(e) => setSubPlayer(i, "name", e.target.value)} data-testid={`bsub-player-name-${s.id}-${i}`} />
                            <input className={inputCls + " w-24 shrink-0"} placeholder="Fargo" value={p.fargo} onChange={(e) => setSubPlayer(i, "fargo", e.target.value)} data-testid={`bsub-player-fargo-${s.id}-${i}`} />
                            <input className={inputCls + " w-28 shrink-0"} placeholder="Robustness" value={p.robustness} onChange={(e) => setSubPlayer(i, "robustness", e.target.value)} data-testid={`bsub-player-rob-${s.id}-${i}`} />
                            <button type="button" onClick={() => removeSubPlayer(i)} className="text-[#999] hover:text-red-500 p-2 shrink-0"><X className="h-4 w-4" /></button>
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={addSubPlayer} data-testid={`bsub-add-player-${s.id}`} className="mt-2 inline-flex items-center gap-1 text-sm text-[#1E63C4] font-medium"><Plus className="h-4 w-4" /> Add player</button>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#555] mb-1">Comment</label>
                      <textarea rows={3} className={inputCls + " resize-y"} placeholder="Comment / notes" value={subDraft.comment} onChange={(e) => setSub("comment", e.target.value)} data-testid={`bsub-comment-${s.id}`} />
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => saveSubEdit(s.id)} disabled={savingSub} data-testid={`bsub-save-${s.id}`} className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                        {savingSub ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                      </button>
                      <button onClick={cancelSubEdit} data-testid={`bsub-cancel-${s.id}`} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-4 py-2 hover:bg-[#F9FAF8]"><X className="h-4 w-4" /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#111]">{s.contact_name} {s.home_location && <span className="text-sm font-normal text-[#999]">· {s.home_location}</span>}</p>
                        <p className="text-sm text-[#555] flex flex-wrap gap-x-4 mt-0.5">
                          <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{s.phone}</span>
                          <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{s.email}</span>
                        </p>
                        {s.created_at && <p className="text-xs text-[#999] mt-1 inline-flex items-center gap-1" data-testid={`braggers-submission-date-${s.id}`}><Calendar className="h-3.5 w-3.5" /> Submitted {fmtDate(s.created_at)}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => startSubEdit(s)} data-testid={`braggers-submission-edit-btn-${s.id}`} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]"><Pencil className="h-4 w-4" /> Edit</button>
                        <button onClick={() => delSub(s.id)} data-testid={`braggers-submission-delete-${s.id}`} className="text-[#999] hover:text-red-500 p-1.5"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    {s.players?.length ? (
                      <ul className="mt-2 text-sm text-[#555] grid sm:grid-cols-2 gap-x-6">
                        {s.players.map((p, idx) => <li key={idx}>{p.name}{p.fargo ? ` — Fargo ${p.fargo}` : ""}{p.robustness ? `, Robustness ${p.robustness}` : ""}</li>)}
                      </ul>
                    ) : null}
                    {s.comment && <p className="mt-2 text-sm text-[#555] flex items-start gap-1.5" data-testid={`braggers-submission-comment-${s.id}`}><MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#1E63C4]" /> {s.comment}</p>}
                  </>
                )}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[#777]">No submissions yet.</p>}
      </div>

      <div className="flex justify-start">
        <button onClick={save} disabled={saving} data-testid="braggers-save-bottom-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </div>
    </div>
  );
}
